import { useState, useEffect } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import {
  Button,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableCell,
  TableBody,
  TextField,
  Checkbox,
} from "@mui/material";
import Addicon from "@mui/icons-material/Add";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import useReferencingCategory from "$hooks/useReferencingCategory";
import { Text } from "$components";
import JSelectDisplayOption from "../JSelectDisplayOption/JSelectDisplayOption";
import JModal from "../JModal/JModal";
import GenericSelect from "$forms/GenericSelect";
import { useForceRefreshProducts } from "$services/Products.service";

import { Loader } from "$components/Layout/Loader";
import { theme } from "$utils/theme/bataillog";

const useStyles = createUseStyles(() => ({
  topFormTable: {
    textTransform: "uppercase",
    background: `${theme.color.gray1} 0% 0% no-repeat padding-box`,
    borderRadius: "3px 3px 0px 0px",
    "& th": {
      color: theme.color.gray5,
      lineHeight: "16px",
      fontSize: 13,
    },
    "& th, td": {
      padding: "4px 12px !important",
    },
  },
  productsTableCellHead: {
    minHeight: 40,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  tdPaddingXs: {
    padding: "8px 4px",
  },
  trWithoutBorder: {
    "& td": {
      borderBottom: "none",
    },
  },
}));

enum ERROR_MESSAGES {
  SUBSTITUTION_QTY = "Erreur sur la quantité à substituer. Veuillez renseigner une quantité supérieure à 0 et inférieure ou égale à la quantité substituable.",
}

type Props = {
  currentCompany: any;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  productToSubstitute: any;
  idEdb: string;
  opList: any;
  substituteProduct: (
    idEdb: string,
    productId: string,
    substitutionQty: number,
    substitutions: any
  ) => any;
  openSnackbar: any;
  isLoading?: boolean;
  setIsLoading?: (value: any) => void;
};

function ModalSubstituteProduct({ isOpen, ...rest }: Props) {
  if (isOpen) {
    return <ModalSubstituteProductInside isOpen={isOpen} {...rest} />;
  }
  return null;
}

function ModalSubstituteProductInside({
  isOpen,
  setOpen,
  productToSubstitute,
  idEdb,
  substituteProduct,
  currentCompany,
  opList,
  isLoading,
  openSnackbar,
  setIsLoading,
}: Props) {
  const classes = useStyles();
  const [productsOptions, setProductsOptions] = useState<any[]>();
  const { products, isLoading: isLoadProduct } = useForceRefreshProducts(
    currentCompany?._id
  );

  const [opProduct, setOpProduct] = useState<any>();
  const [error, setError] = useState(undefined);
  const { referencingCategory } = useReferencingCategory(currentCompany?._id);

  const { control, setValue, getValues, watch, reset } = useForm({
    mode: "onChange",
    // defaultValues: substituteProduct,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "sbProduct",
  });

  const sbProducts = watch("sbProduct");
  watch("substitutionQty");

  // On récupère les produits de substitutions de base
  useEffect(() => {
    remove();
    productToSubstitute?.relatedProduct?.substitutionProducts?.forEach(
      (p: any) => {
        append({
          qty: undefined,
          relatedProduct: p._id,
          defaultSubstitution: true,
        });
      }
    );
  }, [productToSubstitute, append, remove]);

  // On récupère le produit dans l'op
  useEffect(() => {
    if (opList !== null) {
      opList?.forEach((op: any) => {
        op?.products?.forEach((opProduct: any) => {
          if (
            opProduct?.relatedProduct?._id?.toString() ===
            productToSubstitute?.relatedProduct?._id?.toString()
          ) {
            setOpProduct(opProduct);
          }
        });
      });
    }
  }, [opList, productToSubstitute]);

  /*
  //TODO finish this patch
  const fetchProductList = useCallback(async () => {
    const filter = {
      isReferenced: true,
      company: currentCompany?._id,
      categories: referencingCategory?.id,
      notWantedId: [
        productToSubstitute?.relatedProduct?._id,
        ...(productToSubstitute?.relatedProduct?.substitutionProducts?.map(
          (p) => p._id
        ) ?? []),
      ],
    };
    console.log("filter", filter);
    // await queryClient.fetchQuery(["getProductStock"]);
  }, [
    currentCompany?._id,
    productToSubstitute?.relatedProduct?._id,
    productToSubstitute?.relatedProduct?.substitutionProducts,
    referencingCategory?.id,
  ]);
  */

  // On définit les options de la drop down de produits
  useEffect(() => {
    setProductsOptions(
      (products || []).filter((p: any) => {
        return (
          p.isReferenced === true &&
          p.company === currentCompany?._id &&
          p.categories.every(
            (categoryId) => categoryId._id !== referencingCategory?.id
          ) &&
          !productToSubstitute?.relatedProduct?.substitutionProducts?.find(
            (sbP: any) => sbP._id === p._id
          ) &&
          p._id !== productToSubstitute?.relatedProduct?._id
        );
      })
    );
  }, [currentCompany, products, productToSubstitute, referencingCategory?.id]);

  const handleSubstituteProducts = async () => {
    setError(undefined);
    const substitutionValues = getValues();

    if (
      !substitutionValues.substitutionQty ||
      substitutionValues.substitutionQty < 0 ||
      substitutionValues.substitutionQty >
        productToSubstitute.qty - (opProduct?.qty ?? 0)
    ) {
      setError(ERROR_MESSAGES.SUBSTITUTION_QTY);
      return null;
    }

    const finalSubstitutions = substitutionValues.sbProduct.filter(
      (subValue: any) => subValue.qty
    );

    const res: any = await substituteProduct(
      idEdb,
      productToSubstitute._id,
      substitutionValues.substitutionQty,
      finalSubstitutions
    );
    if (res?.error) {
      setError(res.error);
      return null;
    }

    setOpen(false);
    openSnackbar(
      `Le produit ${productToSubstitute?.relatedProduct?.reference} à bien été substitué.`,
      5000
    );

    return null;
  };

  // On vide le tableau des produits lorsqu'on quitte la modale
  const handleQuitModal = () => {
    setOpen(false);
    remove();
    reset();
    productToSubstitute?.relatedProduct?.substitutionProducts?.forEach(
      (p: any) => {
        append({
          qty: undefined,
          relatedProduct: p._id,
          defaultSubstitution: true,
        });
      }
    );
    /* setProductsOptions(
      (products || []).filter(
        (p: any) =>
          p.isReferenced === true &&
          p.company.toString() === currentCompany.toString() &&
          !productToSubstitute?.relatedProduct?.substitutionProducts.find(
            (sbP: any) => sbP._id === p._id
          ) &&
          p._id !== productToSubstitute?.relatedProduct?._id
      )
    ); */
    setError(undefined);
    setIsLoading(false);
  };
  const checkIfAllSubsAreFilled = () => {
    const substitutionValues = getValues();
    return !!(
      substitutionValues?.sbProduct?.length === 0 ||
      !substitutionValues?.substitutionQty ||
      substitutionValues?.substitutionQty <= 0 ||
      !substitutionValues?.sbProduct.some((product) => product.qty > 0)
    );
  };

  return (
    <div>
      {isOpen && (
        <JModal
          labelOk="Substituer"
          labelCancel="Annuler"
          onConfirm={handleSubstituteProducts}
          onClose={handleQuitModal}
          isLoading={isLoading && isLoadProduct}
          disabledConfirm={checkIfAllSubsAreFilled()}
        >
          {isLoading && <Loader />}
          {!isLoading && (
            <form>
              {productToSubstitute && opList && (
                <>
                  <Text>Réf. produit à substituer : </Text>
                  <Text
                    style={{
                      color: theme.color.blue,
                      fontWeight: "bold",
                    }}
                  >
                    {productToSubstitute?.relatedProduct?.reference}
                  </Text>
                  <br />
                  <Text>Nombre substituable : </Text>
                  <Text
                    style={{
                      color: theme.color.blue,
                      fontWeight: "bold",
                    }}
                  >
                    {productToSubstitute.qty - (opProduct?.qty ?? 0)}
                  </Text>
                </>
              )}
              <div>
                <div
                  style={{
                    paddingBottom: 20,
                    paddingTop: 20,
                    display: "flex",
                    flex: 1,
                    alignItems: "center",
                  }}
                >
                  <Text>Nombre de produit à substituer : </Text>
                  <Controller
                    control={control}
                    name="substitutionQty"
                    render={({ field }) => (
                      <TextField
                        size="small"
                        variant="outlined"
                        {...field}
                        placeholder="0"
                        type="number"
                        onChange={(e: any) => {
                          setValue("substitutionQty", e.target.value);
                        }}
                        autoComplete="off"
                        style={{
                          width: "20%",
                          marginLeft: 20,
                          marginRight: 10,
                        }}
                      />
                    )}
                  />
                  <Text>
                    /{productToSubstitute.qty - (opProduct?.qty ?? 0)}
                  </Text>
                </div>
                <TableContainer>
                  <Table size="small">
                    <TableHead className={classes.topFormTable}>
                      <TableRow>
                        <TableCell padding="none">
                          <Text>Référence Produit</Text>
                        </TableCell>
                        <TableCell padding="none" style={{ width: 100 }}>
                          <div className={classes.productsTableCellHead}>
                            Qté
                          </div>
                        </TableCell>
                        <TableCell
                          padding="none"
                          style={{
                            width: 20,
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <div className={classes.productsTableCellHead} />
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields &&
                        fields
                          .filter(
                            (f: any) =>
                              products?.find(
                                (p: any) => p._id === f.relatedProduct
                              )?.isReferenced || f.relatedProduct === undefined
                          )
                          .map((sbProduct: any, index: number) => (
                            <>
                              <TableRow
                                key={sbProduct?.relatedProduct || index}
                                className={
                                  !sbProduct.defaultSubstitution
                                    ? classes.trWithoutBorder
                                    : classes.tdPaddingXs
                                }
                              >
                                <TableCell>
                                  <div style={{ flex: 1 }}>
                                    {sbProduct.defaultSubstitution ? (
                                      productToSubstitute?.relatedProduct?.substitutionProducts?.find(
                                        (sbP: any) =>
                                          sbP._id === sbProduct.relatedProduct
                                      )?.reference
                                    ) : (
                                      <Controller
                                        control={control}
                                        name={`sbProduct.${index}.relatedProduct`}
                                        render={({ field }) => (
                                          <GenericSelect
                                            label="Réf. produit"
                                            required
                                            key={field.value}
                                            valueKey="_id"
                                            placeholder="Réf. produit"
                                            getOptionLabel={(option: any) =>
                                              `${option.reference} - ${option.designation}`
                                            }
                                            disabled={
                                              sbProduct.defaultSubstitution
                                            }
                                            renderOption={(props: any) => (
                                              <JSelectDisplayOption
                                                value1={props.reference}
                                                value2={props.designation}
                                              />
                                            )}
                                            options={productsOptions || []}
                                            {...field}
                                          />
                                        )}
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell padding="none">
                                  <Controller
                                    control={control}
                                    name={`sbProduct.${index}.qty`}
                                    render={({ field }) => (
                                      <TextField
                                        variant="outlined"
                                        {...field}
                                        placeholder="0"
                                        onChange={(e: any) => {
                                          setValue(
                                            `sbProduct.${index}.qty`,
                                            e.target.value
                                          );
                                        }}
                                        disabled={
                                          !sbProducts[index].relatedProduct
                                        }
                                        autoComplete="off"
                                      />
                                    )}
                                  />
                                </TableCell>
                                <TableCell padding="none">
                                  {!sbProduct.defaultSubstitution ? (
                                    <Button
                                      onClick={() => {
                                        remove(index);
                                      }}
                                    >
                                      <DeleteIcon
                                        style={{
                                          minWidth: 30,
                                          color: theme.color.blueLight,
                                        }}
                                      />
                                    </Button>
                                  ) : null}
                                </TableCell>
                              </TableRow>
                              {!sbProduct.defaultSubstitution && (
                                <TableRow>
                                  <TableCell colSpan={3}>
                                    <Controller
                                      name={`sbProduct.${index}.setToPermanent`}
                                      control={control}
                                      rules={{ required: true }}
                                      render={({ field }) => (
                                        <div>
                                          <Checkbox
                                            color="primary"
                                            disabled={
                                              !sbProducts[index].relatedProduct
                                            }
                                            inputProps={{
                                              "aria-label":
                                                "secondary checkbox",
                                            }}
                                            {...field}
                                          />
                                          <Text>
                                            Ajouter comme produit de
                                            substitution permanent
                                          </Text>
                                        </div>
                                      )}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button
                  color="primary"
                  variant="text"
                  onClick={() => append({ relatedProduct: undefined })}
                >
                  <Addicon style={{ marginRight: 4 }} />
                  <Text>Ajouter un produit du catalogue</Text>
                </Button>
              </div>
            </form>
          )}
          {error && <Text style={{ color: "red" }}>{error}</Text>}
        </JModal>
      )}
    </div>
  );
}

export default ModalSubstituteProduct;
